import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  Wrench,
  FileText,
  CalendarClock,
  AlertTriangle,
  DollarSign,
  Plus,
  ArrowRight,
  Mail,
  ChevronRight,
  Upload,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, StatCard, UsageMeter, EmptyState, Skeleton } from '@/components/ui/card';
import { StatusBadge } from '@/components/ui/status-badge';
import { PageHeader } from '@/components/ui/page-header';
import { Avatar } from '@/components/ui/avatar';
import { api, formatCurrency } from '@/lib/utils';
import { DemoDataBanner } from '@/legacy/components/demo-data-banner';
import { importSpreadsheetSearch } from '@/lib/import-search';

export function DashboardPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api<{
      stats: { activeCount: number; dueSoon: number; overdue: number; mrrAtRisk: number };
      attention: Array<{
        id: string;
        contactName: string;
        planName: string;
        endDate: string;
        relativeEndDate: string;
        displayStatus: string;
        amount: number;
      }>;
      activity: Array<{ templateKey: string; recipientEmail: string; sentAt: string; status: string }>;
      usage: { agreements: { used: number; limit: number; percent: number } };
    }>('/api/v1/dashboard'),
  });

  return (
    <div className="space-y-6">
      <DemoDataBanner />
      <PageHeader
        title="Dashboard"
        description="Your maintenance agreements at a glance."
        actions={
          <div className="flex flex-wrap gap-2">
            <Link to="/retention" search={importSpreadsheetSearch('agreements') as never}>
              <Button variant="secondary">
                <Upload className="h-4 w-4" /> Import from spreadsheet
              </Button>
            </Link>
            <Link to="/agreements/new">
              <Button>
                <Plus className="h-4 w-4" /> Add agreement
              </Button>
            </Link>
          </div>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Active agreements" value={data?.stats.activeCount ?? 0} icon={FileText} tone="default" loading={isLoading} to="/agreements" search={{ tab: 'active' }} />
        <StatCard label="Due in 30 days" value={data?.stats.dueSoon ?? 0} icon={CalendarClock} tone="warning" loading={isLoading} to="/agreements" search={{ tab: 'due_soon' }} />
        <StatCard label="Lapsed / overdue" value={data?.stats.overdue ?? 0} icon={AlertTriangle} tone="danger" loading={isLoading} to="/agreements" search={{ tab: 'overdue' }} />
        <StatCard label="Revenue at risk" value={formatCurrency(data?.stats.mrrAtRisk ?? 0)} icon={DollarSign} tone="success" hint="Renewals due in 30 days" loading={isLoading} to="/retention" />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Card
            title="Needs attention"
            description="Agreements due soon or overdue"
            noPadding
            action={
              <Link to="/agreements" search={{ tab: 'due_soon' } as never}>
                <Button variant="ghost" size="sm">
                  View all <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            }
          >
            {isLoading ? (
              <div className="divide-y divide-border">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="flex items-center gap-3 px-5 py-3.5">
                    <Skeleton className="h-9 w-9 rounded-full" />
                    <div className="flex-1 space-y-1.5">
                      <Skeleton className="h-3.5 w-40" />
                      <Skeleton className="h-3 w-24" />
                    </div>
                    <Skeleton className="h-6 w-20 rounded-full" />
                  </div>
                ))}
              </div>
            ) : data?.attention?.length ? (
              <div className="divide-y divide-border">
                {data.attention.map((row) => (
                  <Link
                    key={row.id}
                    to="/agreements"
                    search={{ selected: row.id } as never}
                    className="flex items-center gap-3 px-5 py-3.5 transition-colors hover:bg-bg-subtle/60"
                  >
                    <Avatar name={row.contactName} size="md" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{row.contactName}</p>
                      <p className="truncate text-[13px] text-text-secondary">{row.planName}</p>
                    </div>
                    <div className="hidden text-right sm:block">
                      <p className="text-sm font-medium tabular-nums">{formatCurrency(row.amount ?? 0)}</p>
                      <p className="text-xs text-text-muted">{row.relativeEndDate}</p>
                    </div>
                    <StatusBadge status={row.displayStatus} />
                    <ChevronRight className="h-4 w-4 shrink-0 text-text-muted" />
                  </Link>
                ))}
              </div>
            ) : (
              <EmptyState
                icon={Wrench}
                title="You're all caught up"
                description="No agreements are due in the next 30 days. Nice work."
                action={
                  <Link to="/agreements/new">
                    <Button>
                      <Plus className="h-4 w-4" /> Add agreement
                    </Button>
                  </Link>
                }
              />
            )}
          </Card>
        </div>

        <div className="space-y-6">
          {data?.usage && (
            <Card title="Plan usage">
              <UsageMeter
                label="Active agreements"
                used={data.usage.agreements.used}
                limit={data.usage.agreements.limit}
                percent={data.usage.agreements.percent}
              />
            </Card>
          )}

          <Card title="Recent activity" noPadding>
            {data?.activity?.length ? (
              <ul className="divide-y divide-border">
                {data.activity.slice(0, 6).map((a, i) => (
                  <li key={i} className="flex items-start gap-3 px-5 py-3">
                    <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-subtle text-brand">
                      <Mail className="h-3.5 w-3.5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[13px] capitalize text-text-primary">
                        {a.templateKey.replace(/_/g, ' ')}
                      </p>
                      <p className="truncate text-xs text-text-muted">{a.recipientEmail}</p>
                    </div>
                    <span className="shrink-0 text-xs text-text-muted">
                      {new Date(a.sentAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="px-5 py-10 text-center">
                <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-bg-subtle">
                  <Mail className="h-5 w-5 text-text-muted" />
                </div>
                <p className="text-sm text-text-secondary">No reminders sent yet.</p>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
